import { formatINR, formatDate } from "@/lib/format";

const modeLabels = {
  cash: "Cash",
  upi: "PhonePe / GPay (UPI)",
  rtgs: "RTGS / NEFT / IMPS",
  loan: "Bank Loan (Pending)",
};

export default function PrintBill({ vehicle, notes }) {
  if (!vehicle) return null;
  const t = vehicle.totals || {};
  const advIn = (vehicle.advances || []).filter((a) => a.direction === "in");
  const advOut = (vehicle.advances || []).filter((a) => a.direction === "out");
  const payments = vehicle.payments || [];

  return (
    <div id="print-bill">
      <div style={{ position: "relative", maxWidth: "800px", margin: "0 auto", padding: "32px", fontFamily: "Manrope, sans-serif", color: "#0F172A" }}>
        {/* Watermark */}
        <div
          style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            opacity: 0.06, zIndex: 0, pointerEvents: "none",
          }}
        >
          <img src="/logo.png" alt="" style={{ width: "420px", height: "420px" }} />
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", borderBottom: "3px solid #1E3A8A", paddingBottom: "16px" }}>
            <img src="/logo.png" alt="A1" style={{ width: "70px", height: "70px", borderRadius: "50%" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: "30px", fontWeight: 900, letterSpacing: "-0.5px", color: "#1E3A8A" }}>
                A1 CARS AGENCY
              </div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>
                Used Car Sales &amp; Purchase • Since 1981<br />
                Main Road, City • Phone: +91 90000 00000
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: "13px", fontWeight: 700, color: "#475569" }}>
              <div>SALE INVOICE</div>
              <div>Date: {formatDate(new Date())}</div>
            </div>
          </div>

          {/* Customer & Vehicle */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "20px" }}>
            <Box title="Customer">
              <Line label="Name" value={vehicle.customer_name || "—"} />
              <Line label="Mobile" value={vehicle.customer_mobile || "—"} />
            </Box>
            <Box title="Vehicle">
              <Line label="Reg No" value={vehicle.reg_no} />
              <Line label="Model" value={vehicle.model} />
              <Line label="Year / Color" value={`${vehicle.year || "—"} / ${vehicle.color || "—"}`} />
            </Box>
          </div>

          {/* Payment breakdown */}
          <div style={{ marginTop: "20px" }}>
            <div style={{ fontWeight: 800, fontSize: "15px", marginBottom: "8px" }}>Payment Breakdown</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#1E3A8A", color: "#fff" }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>UTR / Ref</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {advIn.map((a) => (
                  <tr key={a.id} style={rowStyle}>
                    <td style={tdStyle}>{formatDate(a.date)}</td>
                    <td style={tdStyle}>Advance — {modeLabels[a.mode] || a.mode}</td>
                    <td style={tdStyle}>{a.utr || "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatINR(a.amount)}</td>
                  </tr>
                ))}
                {payments.map((p) => (
                  <tr key={p.id} style={rowStyle}>
                    <td style={tdStyle}>{formatDate(p.date)}</td>
                    <td style={tdStyle}>{modeLabels[p.mode] || p.mode}</td>
                    <td style={tdStyle}>{p.utr || "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatINR(p.amount)}</td>
                  </tr>
                ))}
                {advOut.map((a) => (
                  <tr key={a.id} style={rowStyle}>
                    <td style={tdStyle}>{formatDate(a.date)}</td>
                    <td style={{ ...tdStyle, color: "#EA580C" }}>Advance Refund</td>
                    <td style={tdStyle}>{a.narration || "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right", color: "#EA580C" }}>- {formatINR(a.amount)}</td>
                  </tr>
                ))}
                {advIn.length === 0 && payments.length === 0 && (
                  <tr><td style={tdStyle} colSpan={4}>No payments recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
            <div style={{ width: "320px" }}>
              <TotalRow label="Final Selling Price" value={formatINR(vehicle.selling_price)} />
              <TotalRow label="Total Received" value={formatINR(t.total_received)} color="#16A34A" />
              <div style={{ background: t.customer_pending > 0 ? "#FFF7ED" : "#F0FDF4", border: `2px solid ${t.customer_pending > 0 ? "#FED7AA" : "#86EFAC"}`, borderRadius: "10px", padding: "12px", marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 800 }}>Pending Balance Due</span>
                <span style={{ fontWeight: 900, fontSize: "20px", color: t.customer_pending > 0 ? "#EA580C" : "#16A34A" }}>
                  {formatINR(t.customer_pending)}
                </span>
              </div>
            </div>
          </div>

          {/* Narration */}
          <div style={{ marginTop: "20px", fontSize: "13px" }}>
            <div style={{ fontWeight: 800, marginBottom: "4px" }}>Notes / Terms</div>
            <div style={{ whiteSpace: "pre-wrap", color: "#334155", minHeight: "40px", border: "1px dashed #CBD5E1", borderRadius: "8px", padding: "10px" }}>
              {notes || "Vehicle sold on as-is-where-is basis. RC transfer subject to full payment."}
            </div>
          </div>

          {/* Signatures */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "56px", fontSize: "13px", fontWeight: 700 }}>
            <div style={{ borderTop: "1px solid #0F172A", paddingTop: "6px", width: "200px", textAlign: "center" }}>Customer Signature</div>
            <div style={{ borderTop: "1px solid #0F172A", paddingTop: "6px", width: "200px", textAlign: "center" }}>Authorized Signatory</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const thStyle = { padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: "13px" };
const tdStyle = { padding: "8px 10px", borderBottom: "1px solid #E2E8F0" };
const rowStyle = {};

function Box({ title, children }) {
  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: "10px", padding: "12px", background: "#F8FAFC" }}>
      <div style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px", color: "#94A3B8", marginBottom: "6px" }}>{title}</div>
      {children}
    </div>
  );
}
function Line({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", padding: "2px 0" }}>
      <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </div>
  );
}
function TotalRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "14px" }}>
      <span style={{ fontWeight: 600, color: "#475569" }}>{label}</span>
      <span style={{ fontWeight: 800, color: color || "#0F172A" }}>{value}</span>
    </div>
  );
}
